/**
 * Type lists and their patterns (migration 918; docs/9-exploration/TYPE-PATTERNS-PRD.md).
 *
 * A type list is a way a department classifies items: Work types (engineering),
 * Launch types (marketing), Investment types (finance), or a list a workspace
 * adds. Each type in a list may carry a pattern — what usually comes with work
 * of that type — in four parts:
 *
 *   certainty  how sure we are: schedule slack, a confidence ceiling, what must
 *              be true before scheduling. For work types the approach and the
 *              effort multiplier stay on the work type's own row (canon 111).
 *   shape      what usually happens, in order: stages with usual lengths.
 *   workload   which teams, how many capacity units, and in which weeks.
 *   bar        how fixed the date is, the quality bar, who signs off.
 *
 * Shape and workload hang off an anchor: the item's start, or its date.
 */

export const BUILTIN_TYPE_LIST_KEYS = ['work_type', 'launch_type', 'investment_type'] as const;
export type BuiltinTypeListKey = (typeof BUILTIN_TYPE_LIST_KEYS)[number];

export type PatternPart = 'certainty' | 'shape' | 'workload' | 'bar';
export const PATTERN_PARTS: PatternPart[] = ['certainty', 'shape', 'workload', 'bar'];

export type PatternAnchor = 'start' | 'date';
export type DateFirmness = 'fixed' | 'target' | 'flexible';
export type QualityBar = 'polished' | 'good_enough' | 'rough';

/** Canon 111 §3.3 approach kinds. */
export const APPROACH_KINDS = [
  'experimental',
  'diagnostic',
  'iterative',
  'incremental',
  'runbook',
  'validating',
  'replicative',
] as const;
export type ApproachKind = (typeof APPROACH_KINDS)[number];

export interface CertaintyPart {
  /** Weeks a placed window may slip before the slip counts as hard. */
  scheduleSlackWeeks?: number;
  /** The highest calendar confidence (0–1) work of this type can carry. */
  confidenceCeiling?: number;
  /** Not stored for work types — read from the work type's own row. */
  approach?: ApproachKind;
  /** Not stored for work types — read from the work type's own row. Applied once, at demand time. */
  effortMultiplier?: number;
  /** What must be true before this work is scheduled. */
  beforeScheduling?: string[];
}

export interface ShapeStage {
  label: string;
  weeks: number;
}

export interface ShapePart {
  anchor?: PatternAnchor;
  stages?: ShapeStage[];
}

/** One team's share of the work. Weeks are counted from the anchor; negative = before it. */
export interface WorkloadLine {
  resourceType: string;
  capacityUnits: number;
  fromWeek: number;
  toWeek: number;
}

export interface WorkloadPart {
  anchor?: PatternAnchor;
  lines?: WorkloadLine[];
}

export interface BarPart {
  date?: DateFirmness;
  quality?: QualityBar;
  /** Displacement order, 1 (slides first) – 10 (slides last). */
  firmness?: number;
  signOff?: string[];
}

export interface PatternParts {
  certainty: CertaintyPart;
  shape: ShapePart;
  workload: WorkloadPart;
  bar: BarPart;
}

export interface TypeList {
  id: string;
  workspaceId: string;
  key: string;
  name: string;
  description: string | null;
  builtin: boolean;
  ownerOrgUnitId: string | null;
  isActive: boolean;
  sortOrder: number;
}

/** A type in any list. Built-in lists read their own catalogs; custom lists read custom_types. */
export interface TypeOption {
  /** custom_types.id for a custom list; null for a built-in list's type. */
  id: string | null;
  value: string;
  label: string;
  description: string | null;
  isActive: boolean;
}

export interface TypePattern extends PatternParts {
  id: string;
  workspaceId: string;
  listKey: string;
  typeValue: string;
  versionNumber: number;
  supersedesId: string | null;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ItemPatternOverride {
  id: string;
  itemId: string;
  part: PatternPart;
  field: string;
  value: unknown;
  reason: string | null;
  setBy: string | null;
  setAt: string;
}

/** An item's type in each list it has one in, keyed by list key. */
export type ItemTypes = Record<string, string | null | undefined>;

export const EMPTY_PATTERN: PatternParts = { certainty: {}, shape: {}, workload: {}, bar: {} };
